<template>
  <div class="demand-notice-wrapper">
    <button class="print-btn" @click="printPage">
      <img src="assets/printer.svg" alt="Printer Icon">
    </button>
    <button class="print-btn email-doc" title="Send On Email" @click="sendEmail">
      <img src="assets/email-icon.png" alt="Email Icon">
    </button>

    <page class="page">
      <div class="page-content">

        <!-- HEADER -->
        <header class="header">
          <div>
            <img :src="state_logo()" class="header-logo">
          </div>
          <div class="header-center">
            <h2 class="county-title">
              County Government Of <br> {{ county.countyName }}
            </h2>
            <h4 class="document-title">Plot Rent Demand Notice</h4>
            <h4 class="notice-type-label">
              <span>{{ getNoticeTypeLabel() }}</span>
            </h4>
            <h4 class="county-address">
              {{ county.address }} <br>
              Email: {{ county.email }}
            </h4>
            <h4 class="county-tagline">
              <span class="tagline-block">{{ county.tagline }}</span>
              <span class="tagline-block">#{{ county.tagline2 }}</span>
            </h4>
          </div>
          <div>
            <img :src="county_logo()" class="header-logo">
          </div>
        </header>

        <div class="divider-red">
          <img src="./assets/red-line.png">
        </div>

        <!-- META INFO -->
        <div class="meta-row">
          <div class="meta-left">
            <p>Printed By <strong>{{ sessionNames }}</strong></p>
            <p>Date Printed <strong>{{ formatDate(showDate()) }}</strong></p>
          </div>
          <div class="meta-right">
            <p>Notice Ref: <strong>{{ noticeRef }}</strong></p>
            <p>Payment Deadline: <strong class="text-danger">{{ formattedDeadline }}</strong></p>
          </div>
        </div>

        <!-- PLOT DETAILS TABLE -->
        <table class="details-table">
          <thead>
          <tr>
            <td colspan="3" class="table-header-cell">Notice Date: {{ formatDate(plot.dateCreated) }}</td>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>
              <span>Plot No.: </span>
              <strong class="text-primary">{{ plot.plotNumber }}</strong>
            </td>
            <td>
              <span>Zone/Market: </span>
              <strong>{{ plot.zone }}</strong>
            </td>
            <td>
              <span>Plot Size: </span>
              <strong>{{ plot.plotSize || 'N/A' }}</strong>
            </td>
          </tr>
          <tr>
            <td>
              <span>Sub County: </span>
              <strong>{{ plot.subCountyName }}</strong>
            </td>
            <td>
              <span>Ward: </span>
              <strong>{{ plot.wardName || 'N/A' }}</strong>
            </td>
            <td>
              <span>Status: </span>
              <strong :style="getStatusColor(plot.status)">{{ plot.status }}</strong>
            </td>
          </tr>
          <tr>
            <td colspan="2">
              <span>Plot Owner: </span>
              <strong>{{ plot.ownerNames }}</strong>
            </td>
            <td>
              <span>ID/Passport: </span>
              <strong>{{ plot.ownerID || 'N/A' }}</strong>
            </td>
          </tr>
          <tr>
            <td>
              <span>Phone: </span>
              <strong>{{ plot.ownerPhoneNo || 'N/A' }}</strong>
            </td>
            <td colspan="2">
              <span>Email: </span>
              <strong class="text-lowercase">{{ plot.ownerEmail || 'N/A' }}</strong>
            </td>
          </tr>
          <tr>
            <td colspan="3">
              <span>Physical Address: </span>
              <strong>{{ plot.plotAddress || 'Not Provided' }}</strong>
            </td>
          </tr>
          </tbody>
        </table>

        <div class="divider-red">
          <img src="./assets/red-line.png">
        </div>

        <!-- CHARGES SECTION -->
        <div class="charges-section">
          <div class="stamp-container">
            <img src="./assets/demand-notice-stamp.png" class="stamp-img">
          </div>

          <div class="charges-content">
            <table class="charges-table">
              <thead>
              <tr>
                <td colspan="2" class="charges-header">Details Of Charges</td>
              </tr>
              </thead>
              <tbody>
              <tr class="row-annual">
                <td class="charge-label charge-label-bold">Annual Plot Rent</td>
                <td class="charge-value text-success-dark">KES {{ numberFormat(parseFloat(plot.annualRent) || 0) }}</td>
              </tr>
              <tr class="row-arrears">
                <td class="charge-label">Arrears Brought Forward</td>
                <td class="charge-value text-warning-dark">KES {{ numberFormat(parseFloat(plot.arrearsForward) || 0) }}</td>
              </tr>
              <tr class="row-billed">
                <td class="charge-label">Total Amount Billed</td>
                <td class="charge-value text-info-dark">KES {{ numberFormat(parseFloat(plot.totalBilled) || 0) }}</td>
              </tr>
              <tr class="row-penalty">
                <td class="charge-label">Accumulated Penalty (25%)</td>
                <td class="charge-value text-danger-dark">KES {{ numberFormat(parseFloat(plot.accumulatedPenalty) || 0) }}</td>
              </tr>
              <tr class="row-payments">
                <td class="charge-label">Total Payments Made</td>
                <td class="charge-value text-teal-dark">KES {{ numberFormat(parseFloat(plot.totalPayments) || 0) }}</td>
              </tr>
              <tr class="row-total">
                <td class="total-label">TOTAL AMOUNT DUE:</td>
                <td class="total-value">KES {{ numberFormat(getCurrentBalance()) }}</td>
              </tr>
              </tbody>
            </table>

            <!-- PAYMENT PRIORITY -->
            <div class="payment-priority-box">
              <h6 class="priority-title">PAYMENT ALLOCATION PRIORITY:</h6>
              <ol class="priority-list">
                <li><strong>Oldest Arrears First:</strong> Previous years' outstanding balances</li>
                <li><strong>Current Year Rent:</strong> KES {{ numberFormat(parseFloat(plot.annualRent) || 0) }}</li>
                <li><strong>Penalties:</strong> KES {{ numberFormat(parseFloat(plot.accumulatedPenalty) || 0) }}</li>
              </ol>
            </div>

            <!-- LEGAL ACTION WARNING -->
            <div v-if="noticeType === 'legal_action'" class="alert-box alert-legal">
              <h6 class="alert-title">⚠️ LEGAL ACTION WARNING</h6>
              <p class="alert-text">
                Failure to settle the outstanding amount by <strong>31st March {{ getMarchYear() }}</strong> will result in:
              </p>
              <ul class="alert-list">
                <li>Legal proceedings being initiated against you</li>
                <li>Additional legal costs being added to your balance</li>
                <li>Potential eviction from the plot</li>
              </ul>
            </div>

            <!-- FINAL NOTICE -->
            <div v-else-if="noticeType === 'final_notice'" class="alert-box alert-final">
              <h6 class="alert-title alert-title-warning">⚠️ FINAL NOTICE</h6>
              <p class="alert-text alert-text-warning">
                This is your <strong>FINAL NOTICE</strong> before escalation to legal action. Please settle your balance immediately to avoid further consequences.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER SECTION -->
      <div class="footer-section">
        <div class="footer-top">
          <div class="footer-notes">
            <h2 class="footer-heading">Please note that the outstanding amount is</h2>
            <ol class="footer-list">
              <li>Payable at the county cash office or by other electronic means clearly outlined by the county</li>
              <li>The total amount will continue to accrue a late payment penalty at 25% per annum.</li>
              <li>Payment allocation follows priority: Oldest Arrears → Current Rent → Penalties</li>
              <li>Legal proceedings may be initiated for non-payment by <strong>31st March {{ getMarchYear() }}</strong>.</li>
            </ol>
            <p class="footer-contact"><strong>Please contact the Revenue Department at county offices for any queries.</strong></p>
          </div>

          <div class="qr-section">
            <div class="scan-label">
              <span class="scan-text">Scan to Pay <br>Instantly!</span>
            </div>
            <div class="qr-code-container">
              <img :src="generateQRCode()" class="qr-img">
              <img src="assets/camera-frame.svg" class="qr-frame">
              <img src="assets/curved-arrow.svg" class="qr-arrow">
            </div>
          </div>
        </div>

        <footer class="page-footer">
          <div class="footer-inner">
            <div class="thank-you-row">
              <span class="thank-you-text">Thank You!</span>
              <img src="assets/blue-line.png" class="blue-line">
            </div>
            <div class="footer-bottom">
              <p>Prepared By <strong>{{ plot.createdBy || sessionNames }}</strong></p>
              <p class="plot-id"><strong>Plot ID: {{ plot.id }}</strong></p>
              <div class="powered-by">
                <p><strong>Powered By</strong></p>
                <img :src="county_logo()" class="powered-logo">
              </div>
            </div>
          </div>
        </footer>
      </div>
    </page>
  </div>
</template>

<script>
/* eslint-disable */

import { execute, plotRent, state_logo, county_logo } from "@/api";
import dayjs from "dayjs";

export default {
  name: "PlotRentDemandNotice",
  data() {
    return {
      sessionNames: "",
      noticeRef: "",
      noticeType: "reminder",
      deadline: "",
      formattedDeadline: "",
      date: null,
      county: {
        countyName: null,
        address: null,
        email: null,
        tagline: null,
        tagline2: null,
      },
      plot: {
        id: null,
        plotNumber: null,
        zone: null,
        subCountyName: null,
        subCountyID: null,
        wardName: null,
        wardID: null,
        plotSize: null,
        plotAddress: null,
        ownerNames: null,
        ownerID: null,
        ownerPhoneNo: null,
        ownerEmail: null,
        annualRent: 0,
        arrearsForward: 0,
        totalBilled: 0,
        accumulatedPenalty: 0,
        totalPayments: 0,
        currentBalance: 0,
        status: null,
        createdBy: null,
        dateCreated: null,
      },
    };
  },
  mounted() {
    this.sessionNames =
        sessionStorage.getItem("sessionNames") || "System User";

    try {
      const params = JSON.parse(localStorage["demandNoticeParams"]);
      if (params.plotId) {
        this.noticeType = params.noticeType || "reminder";
        this.deadline = params.deadline || "";
        this.noticeRef = params.noticeRef || this.generateNoticeRef();
        this.getPlotForDemandNotice(params.plotId);
      } else {
        alert("No plot ID provided");
      }
    } catch (error) {
      const hash = window.location.hash;
      const plotIdFromURL = hash.split("/")[3];

      if (plotIdFromURL) {
        this.noticeRef = this.generateNoticeRef();
        this.getPlotForDemandNotice(plotIdFromURL);
      } else {
        alert("No plot information found");
      }
    }

    this.setFormattedDeadline();
    this.menuAccess();
  },
  methods: {
    state_logo() {
      return state_logo;
    },
    county_logo() {
      return county_logo;
    },

    printPage() {
      window.print();
    },

    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Plot Rent Demand Notice");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, plotRent)
          .then(() => {})
          .catch(() => {});
    },

    generateNoticeRef() {
      const timestamp = Date.now().toString().slice(-10);
      const random = Math.floor(Math.random() * 1000)
          .toString()
          .padStart(3, "0");
      return `DN${timestamp}${random}`;
    },

    numberFormat(num) {
      if (num === null || num === undefined || isNaN(num)) {
        return "0";
      }
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(parseFloat(num));
    },

    formatDate(dateString) {
      if (!dateString) return dayjs().format("DD MMM YYYY [AT] hh:mm A");
      const date = dayjs(dateString);
      return date.format("DD MMM YYYY [AT] hh:mm A");
    },

    showDate() {
      return new Date();
    },

    getMarchYear() {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      return currentMonth > 3 ? currentYear + 1 : currentYear;
    },

    setFormattedDeadline() {
      const marchYear = this.getMarchYear();
      this.formattedDeadline = `31st March ${marchYear}`;
      this.deadline = `${marchYear}-03-31`;
    },

    getNoticeTypeLabel() {
      const labels = {
        reminder: "Friendly Reminder",
        first_notice: "First Demand Notice",
        final_notice: "Final Demand Notice",
        legal_action: "Legal Action Warning",
      };
      return labels[this.noticeType] || "Demand Notice";
    },

    getStatusColor(status) {
      const colors = {
        Active: "color: #28a745;",
        Suspended: "color: #dc3545;",
        Vacant: "color: #6c757d;",
        Transferred: "color: #17a2b8;",
      };
      return colors[status] || "color: #333;";
    },

    getCurrentBalance() {
      if (
          this.plot.currentBalance &&
          parseFloat(this.plot.currentBalance) > 0
      ) {
        return parseFloat(this.plot.currentBalance);
      }

      const arrears = parseFloat(this.plot.arrearsForward) || 0;
      const billed = parseFloat(this.plot.totalBilled) || 0;
      const penalty = parseFloat(this.plot.accumulatedPenalty) || 0;
      const payments = parseFloat(this.plot.totalPayments) || 0;

      return arrears + billed + penalty - payments;
    },

    generateQRCode() {
      const amount = this.getCurrentBalance();
      const plotRef = this.plot.plotNumber || this.plot.id;
      const zone = this.plot.zone || "";

      const qrData = `Plot: ${plotRef} | Zone: ${zone} | Owner: ${this.plot.ownerNames} | Amount: KES ${this.numberFormat(amount)}`;
      return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}`;
    },

    getPlotForDemandNotice(plotId) {
      const data = new FormData();
      data.append("function", "getPlotForDemandNotice");
      data.append("plotId", plotId);

      execute(data, plotRent)
          .then((res) => {
            if (res.data.success) {
              this.plot = res.data.data.plot;
              this.county = res.data.data.county || this.county;
            } else {
              alert("Error loading plot: " + res.data.message);
            }
          })
          .catch((e) => {
            console.error("Error fetching plot:", e);
            alert("Error loading plot data: " + e.message);
          });
    },

    sendEmail() {
      alert("Email functionality - implement as needed");
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Yellowtail&display=swap');

* {
  box-sizing: border-box;
  font-family: 'Montserrat', sans-serif;
  -webkit-print-color-adjust: exact !important;
  color-adjust: exact !important;
}

/* ── PRINT BUTTONS ── */
.print-btn {
  display: flex;
  z-index: 1000000000;
  position: fixed;
  background: #4aaa23;
  color: white;
  top: 30px;
  right: 30px;
  border-radius: 50%;
  padding: 1rem;
  margin: 0;
  border: none;
  justify-content: center;
  align-items: center;
  box-shadow: 1px 0 20px 4px rgba(136, 136, 136, 0.65);
  cursor: pointer;
  transition: 0.4s;
}

.print-btn img {
  height: 35px;
}

.email-doc {
  top: 130px;
  background: #0063c0;
}

/* ── WRAPPER ── */
.demand-notice-wrapper {
  padding: 0;
  margin: 0;
  background-color: #a2a2a21c;
}

/* ── PAGE ── */
.page {
  width: 210mm;
  height: 297mm;
  display: flex;
  position: relative;
  flex-flow: column;
  background-color: white;
  overflow: hidden;
  margin: 0 auto;
}

.page-content {
  padding: 6mm 8.5mm;
}

/* ── HEADER ── */
.header {
  display: flex;
  height: 38mm;
  align-items: center;
  position: relative;
  width: 100%;
  justify-content: space-between;
}

.header-logo {
  height: 30mm;
  margin-right: 0;
}

.header-center {
  text-align: center;
  margin-right: 8.5mm;
  margin-left: 8.5mm;
}

.county-title {
  margin-top: 0;
  font-size: 24px;
  text-transform: uppercase;
  color: #4ca82c;
  font-weight: 700;
  margin-bottom: 1mm;
  padding-bottom: 0;
}

.document-title {
  padding: 0;
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  text-transform: uppercase;
  color: #dc3545;
}

.notice-type-label {
  padding: 0;
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  font-style: italic;
  font-family: 'Times New Roman', Times, serif;
  margin-top: 1.5mm;
  margin-bottom: 3mm;
}

.county-address {
  padding: 0;
  margin: 0;
  font-size: 12px;
  font-weight: 400;
}

.county-tagline {
  padding: 0;
  margin: 0;
  font-size: 11px;
  font-weight: 400;
  font-style: italic;
  font-family: 'Times New Roman', Times, serif;
  margin-top: 1.5mm;
  line-height: 1.3;
}

.tagline-block {
  font-style: italic;
  font-family: 'Times New Roman', Times, serif;
  display: block;
  margin-bottom: 0.5mm;
}

/* ── DIVIDERS ── */
.divider-red img {
  width: 100%;
}

/* ── META ROW ── */
.meta-row {
  display: flex;
  justify-content: space-between;
}

.meta-left,
.meta-right {
  font-size: 11px;
  margin-top: 1mm;
}

.meta-left p,
.meta-right p {
  padding: 0;
  margin: 0;
  padding-bottom: 1mm;
}

.meta-left strong,
.meta-right strong {
  text-transform: uppercase;
}

.meta-right {
  text-align: right;
}

/* ── TABLES ── */
.details-table {
  margin-top: 4mm;
  border-spacing: initial;
  width: 100%;
  table-layout: fixed;
}

.details-table thead {
  text-transform: uppercase;
  font-weight: 700;
  font-size: 11px;
  background: #9e9e9e2e;
}

.details-table tbody {
  font-size: 11px;
  text-transform: uppercase;
}

.table-header-cell {
  padding: 2mm;
}

td {
  padding: 8px;
  border: 1px solid #00000014;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* ── CHARGES SECTION ── */
.charges-section {
  display: flex;
  position: relative;
  margin-top: 2mm;
}

.stamp-container {
  position: absolute;
  left: -5mm;
  top: 8mm;
  z-index: 1;
}

.stamp-img {
  height: 26mm;
  transform: rotate(45deg);
  opacity: 0.6;
}

.charges-content {
  width: 100%;
  margin-left: 36mm;
}

.charges-table {
  border-spacing: 0;
  text-transform: capitalize;
  font-size: 11px;
  width: 100%;
}

.charges-header {
  padding: 2mm;
  text-align: center;
  border: 1px solid #d9d9d9;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 11px;
  background: #9e9e9e2e;
}

.charge-label {
  padding: 2mm;
  border: 1px solid #d9d9d9;
  width: 60%;
}

.charge-label-bold {
  font-weight: bold;
}

.charge-value {
  text-align: right;
  padding: 2mm;
  border: 1px solid #d9d9d9;
  font-weight: bold;
  width: 40%;
}

.row-annual { background: #e8f5e8; }
.row-arrears { background: #fff3cd; }
.row-billed { background: #e3f2fd; }
.row-penalty { background: #f8d7da; }
.row-payments { background: #d1ecf1; }

.row-total {
  background: #343a40;
  color: white;
}

.total-label {
  padding: 2mm;
  font-weight: bold;
  border: 1px solid #d9d9d9;
  color: white;
  font-size: 13px;
}

.total-value {
  text-align: right;
  padding: 2mm;
  color: #ffeb3b;
  font-weight: bold;
  font-size: 16px;
  border: 1px solid #d9d9d9;
}

/* ── PAYMENT PRIORITY BOX ── */
.payment-priority-box {
  margin-top: 3mm;
  padding: 2mm;
  background: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 3px;
}

.priority-title {
  margin: 0 0 1mm 0;
  font-size: 10px;
  color: #1565c0;
  font-weight: bold;
}

.priority-list {
  margin: 0;
  padding-left: 4mm;
  font-size: 9px;
  color: #0d47a1;
}

.priority-list li {
  margin-bottom: 0.5mm;
}

.priority-list li:last-child {
  margin-bottom: 0;
}

/* ── ALERT BOXES ── */
.alert-box {
  margin-top: 3mm;
  padding: 3mm;
  border-radius: 3px;
}

.alert-legal {
  background: #f8d7da;
  border: 2px solid #dc3545;
}

.alert-final {
  background: #fff3cd;
  border: 2px solid #ffc107;
}

.alert-title {
  margin: 0 0 1mm 0;
  font-size: 11px;
  color: #721c24;
  font-weight: bold;
}

.alert-title-warning {
  color: #856404;
}

.alert-text {
  margin: 0;
  font-size: 9px;
  color: #721c24;
  line-height: 1.4;
}

.alert-text-warning {
  color: #856404;
}

.alert-list {
  margin: 1mm 0 0 0;
  padding-left: 4mm;
  font-size: 9px;
  color: #721c24;
}

/* ── FOOTER SECTION ── */
.footer-section {
  padding: 5mm 8.5mm;
  width: 100%;
  background-color: #9e9e9e2e;
  flex-grow: 1;
  display: flex;
  flex-flow: column;
  align-items: baseline;
  justify-content: space-between;
}

.footer-top {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.footer-notes {
  font-size: 11px;
  padding-top: 2mm;
}

.footer-heading {
  margin-top: 0;
  font-size: 15px;
  text-transform: uppercase;
  color: #0045a1;
  font-weight: 700;
  margin-bottom: 2mm;
  padding-bottom: 0;
}

.footer-list {
  margin: 0;
  padding-left: 5mm;
}

.footer-list li {
  margin-bottom: 1.5mm;
}

.footer-contact {
  margin-top: 2mm;
  margin-bottom: 0;
}

/* ── QR CODE ── */
.qr-section {
  padding-top: 2mm;
  padding-left: 12mm;
  margin-bottom: 5mm;
}

.scan-label {
  display: flex;
  margin-bottom: 3mm;
  justify-content: flex-end;
  align-items: center;
}

.scan-text {
  font-family: 'Yellowtail', cursive;
  color: #4ca82c;
  line-height: 0.9;
  font-size: 20px;
  text-align: right;
  transform: rotate(37deg);
}

.qr-code-container {
  position: relative;
  display: flex;
}

.qr-img {
  height: 18mm;
  margin: 1mm;
}

.qr-frame {
  position: absolute;
  left: 0;
  height: 20mm;
}

.qr-arrow {
  height: 9mm;
  margin-top: 10px;
}

/* ── PAGE FOOTER ── */
.page-footer {
  position: relative;
  width: 100%;
}

.footer-inner {
  position: absolute;
  bottom: 0;
}

.thank-you-row {
  display: flex;
  flex-flow: column;
}

.thank-you-text {
  font-family: 'Yellowtail', cursive;
  color: #0045a1;
  line-height: 0.9;
  font-size: 18px;
  text-align: right;
}

.blue-line {
  width: 100%;
}

.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1mm;
}

.footer-bottom p {
  padding-bottom: 5px;
  margin-bottom: 0;
  font-size: 11px;
  margin-top: 0;
}

.plot-id {
  text-transform: uppercase;
}

.powered-by {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  margin-left: 8.5mm;
}

.powered-by p {
  font-weight: bold;
}

.powered-logo {
  height: 8mm;
  padding-left: 2mm;
}

/* ── UTILITY CLASSES ── */
.text-primary { color: #0045a1; }
.text-danger { color: #dc3545; }
.text-lowercase { text-transform: lowercase; }
.text-success-dark { color: #28a745; }
.text-warning-dark { color: #856404; }
.text-info-dark { color: #1565c0; }
.text-danger-dark { color: #721c24; }
.text-teal-dark { color: #0c5460; }

/* ── PRINT STYLES ── */
@media print {
  .print-btn {
    display: none;
  }

  .demand-notice-wrapper {
    margin: 0 !important;
    padding: 0 !important;
    background-color: white;
  }

  @page {
    size: A4 portrait;
    margin: 0;
  }
}
</style>